import type { Vec3 } from './model.js';
import type { ProjectDocument, QuarterTurn } from './document.js';
import type { EvaluatedCabinet, EvaluatedProject } from './project.js';
import { partBox } from './geometry.js';
import { buildRoom, wallTopAt, type RoomWallBuild } from './room.js';

/**
 * "Montiraj na zid": a unit (element or assembly) goes with its back against the inner face of a wall.
 * It turns by quarter turns so its front faces into the room, keeps its height, and takes the freest
 * place along the wall — the middle of the wall when it is free, otherwise the middle of the widest
 * gap between the other units and the openings (doors always, windows when the unit reaches them).
 */

const NEAR = 50_000; // units closer than 50 mm to the wall stand at it

export interface WallMount {
  rotationY: QuarterTurn;
  position: Vec3;
  /** Set when the wall is not at a right angle to the grid (the unit is turned to the nearest quarter). */
  warning?: string;
}

/**
 * World box of a unit from its real parts (assemblies may have panels on either side of their origin),
 * with a given turn and position; same transform as the viewer (mirror about the bounds width, then the turn).
 */
export function unitWorldBoxAt(c: EvaluatedCabinet, rotationY: QuarterTurn, position: Vec3): { min: Vec3; max: Vec3 } {
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of c.result.parts) {
    const b = partBox(p);
    for (const k of [0, 1, 2] as const) {
      min[k] = Math.min(min[k], b.min[k]);
      max[k] = Math.max(max[k], b.min[k] + b.size[k]);
    }
  }
  if (!Number.isFinite(min[0])) return { min: [...position], max: [...position] };
  const w = c.result.bounds.size[0];
  const xs = c.instance.transform.mirrored ? [w - max[0], w - min[0]] : [min[0], max[0]];
  const phi = (-rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  const wx: number[] = [];
  const wz: number[] = [];
  for (const x of xs)
    for (const z of [min[2], max[2]]) {
      wx.push(position[0] + x * cos + z * sin);
      wz.push(position[2] - x * sin + z * cos);
    }
  return { min: [Math.min(...wx), position[1] + min[1], Math.min(...wz)], max: [Math.max(...wx), position[1] + max[1], Math.max(...wz)] };
}

/** World box corners in plan (x, z). */
const footprint = (b: { min: Vec3; max: Vec3 }) => [
  [b.min[0], b.min[2]],
  [b.max[0], b.min[2]],
  [b.max[0], b.max[2]],
  [b.min[0], b.max[2]],
];

/** Extent of a world box along the wall (u from the wall start) and towards the room (depth from the inner face). */
function onWall(w: RoomWallBuild, b: { min: Vec3; max: Vec3 }) {
  const us = footprint(b).map(([x, z]) => (x! - w.ax) * w.ux + (z! - w.az) * w.uz);
  // Distance into the room (−n side) from the inner face.
  const ds = footprint(b).map(([x, z]) => -((x! - w.ax) * w.nx + (z! - w.az) * w.nz));
  return { u0: Math.min(...us), u1: Math.max(...us), d0: Math.min(...ds), d1: Math.max(...ds) };
}

export function mountOnWall(doc: ProjectDocument, ev: EvaluatedProject, unitId: string, lineId: string): WallMount | { error: string } {
  const plan = doc.roomPlan;
  if (!plan) return { error: 'Projekat nema prostoriju.' };
  const w = buildRoom(plan).walls.find((x) => x.lineId === lineId);
  if (!w) return { error: 'Izabrana linija nije zid.' };
  const unit = ev.cabinets.find((c) => c.instance.id === unitId);
  if (!unit) return { error: 'Element ne postoji.' };
  // Front of the unit (local +z) in the world after a turn φ = −rotationY: (sin φ, cos φ). It must look into the room (−n).
  let best: { rot: QuarterTurn; dot: number } = { rot: 0, dot: -Infinity };
  for (const rot of [0, 90, 180, 270] as QuarterTurn[]) {
    const phi = (-rot * Math.PI) / 180;
    const dot = Math.sin(phi) * -w.nx + Math.cos(phi) * -w.nz;
    if (dot > best.dot + 1e-9) best = { rot, dot };
  }
  const y = unit.instance.transform.position[1];
  const at0 = unitWorldBoxAt(unit, best.rot, [0, y, 0]);
  const e = onWall(w, at0);
  const width = e.u1 - e.u0;
  const depth = e.d1 - e.d0;
  const bottom = at0.min[1];
  const top = at0.max[1];
  // Obstacles along the wall: other units standing at it, doors, windows the unit would cover.
  const busy: [number, number][] = [];
  for (const c of ev.cabinets) {
    if (c.instance.id === unitId || c.instance.hidden) continue;
    const b = unitWorldBoxAt(c, c.instance.transform.rotationY, c.instance.transform.position);
    const o = onWall(w, b);
    if (o.d0 <= NEAR && o.d1 > 0 && o.d0 < depth && Math.min(b.max[1], top) > Math.max(b.min[1], bottom)) busy.push([o.u0, o.u1]);
  }
  for (const it of plan.items) {
    if (it.lineId !== lineId || (it.kind !== 'door' && it.kind !== 'window')) continue;
    const span = buildRoom(plan).items.find((x) => x.item.id === it.id);
    if (!span) continue;
    if (it.kind === 'door' || Math.min(top, it.elevation + it.height) > Math.max(bottom, it.elevation)) busy.push([span.u0, span.u1]);
  }
  busy.sort((a, b) => a[0] - b[0]);
  const gaps: [number, number][] = [];
  let cur = 0;
  for (const [a, b] of busy) {
    if (a > cur) gaps.push([cur, Math.min(a, w.length)]);
    cur = Math.max(cur, b);
  }
  if (cur < w.length) gaps.push([cur, w.length]);
  const fits = gaps.filter(([a, b]) => b - a >= width - 1);
  const pick = fits.length ? fits.reduce((p, q) => (q[1] - q[0] > p[1] - p[0] + 1 ? q : p)) : null;
  const centre = pick ? (pick[0] + pick[1]) / 2 : w.length / 2;
  // Move so the back touches the inner face (depth 0) and the unit is centred at `centre`.
  const du = centre - (e.u0 + e.u1) / 2;
  const dd = -e.d0;
  const dx = w.ux * du - w.nx * dd;
  const dz = w.uz * du - w.nz * dd;
  const warnings: string[] = [];
  if (best.dot < 0.999) warnings.push('Zid nije pod pravim uglom — element je okrenut najbliže (samo okreti od 90°).');
  if (!pick) warnings.push('Na zidu nema dovoljno slobodnog mjesta — element je na sredini zida.');
  if (top > Math.min(wallTopAt(w, Math.max(0, centre - width / 2)), wallTopAt(w, Math.min(w.length, centre + width / 2))) + 500) warnings.push('Element je viši od zida na tom mjestu.');
  return { rotationY: best.rot, position: [Math.round(dx), y, Math.round(dz)], ...(warnings.length ? { warning: warnings.join(' ') } : {}) };
}
